import type { ReactNode } from 'react';
import { Clock } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export interface TimelineItem {
  id?: string;
  time: string;
  actor?: ReactNode;
  action: ReactNode;
  detail?: ReactNode;
  icon?: ReactNode;
  tone?: 'default' | 'success' | 'warning' | 'danger' | 'info';
}

interface TimelineProps {
  items: TimelineItem[];
  empty?: ReactNode;
}

export function Timeline({ items, empty }: TimelineProps) {
  const { t } = useTranslation('components');
  if (items.length === 0) {
    return (
      <div className="ds-timeline ds-timeline--empty">
        {empty ?? <span style={{ color: 'var(--ds-text-tertiary)' }}>{t('timeline.empty')}</span>}
      </div>
    );
  }
  return (
    <ol className="ds-timeline" aria-label={t('timeline.label')}>
      {items.map((item, idx) => (
        <li
          key={item.id ?? idx}
          className={`ds-timeline__item ds-timeline__item--${item.tone ?? 'default'}`}
        >
          <span className="ds-timeline__rail" aria-hidden>
            <span className="ds-timeline__dot">{item.icon ?? null}</span>
          </span>
          <div className="ds-timeline__body">
            <div className="ds-timeline__head">
              {item.actor ? <strong className="ds-timeline__actor">{item.actor}</strong> : null}
              <span className="ds-timeline__action">{item.action}</span>
            </div>
            {item.detail ? <div className="ds-timeline__detail">{item.detail}</div> : null}
            <div className="ds-timeline__meta">
              <Clock size={11} aria-hidden /> {item.time}
            </div>
          </div>
        </li>
      ))}
    </ol>
  );
}
